import { createContext, useContext } from 'react'
import type { ApiFailure, ApiResult, Schema } from '@/api/client'

export type Tenant = Schema<'TenantOut'>
export type Membership = Schema<'MembershipOut'>
export type TenantMembership = Schema<'TenantMembershipOut'>
export type Role = Schema<'Role'>

export interface TenantValue {
  status: 'loading' | 'ready' | 'error'
  failure: ApiFailure | null
  /** Every organisation this user belongs to — what fills the switcher. */
  memberships: TenantMembership[]
  /** The one the *session* is in. Null until an organisation is selected. */
  active: TenantMembership | null
  /**
   * Go to an organisation — its own host, by handoff (spec: *Sessions and the
   * handoff*). Resolves `ok` once the browser has been told to leave; on the
   * organisation's own host it resolves `ok` at once with nothing to do.
   * `next` is the path to land on there, else the dashboard.
   *
   * The real failure and not a boolean: a boolean has exactly one reason in it,
   * so every way this can fail — a network drop, a 5xx, a 429,
   * `NOT_A_MEMBER`, `WORKSPACE_UNVERIFIED` — reached the user as "you are no
   * longer a member of that organisation", which is both wrong and
   * unactionable (WEB-9). The caller decides which failures deserve that
   * sentence.
   */
  select: (tenantId: string, next?: string) => Promise<ApiResult<void>>
  /**
   * The organisation signing in lands in — what the switcher marks *Default*.
   *
   * **The effective default, not the stored one.** Somebody who has never chosen
   * still has one: the server falls back to their oldest membership
   * (`api/app/tenancy/default_org.py`), and that is where they will actually
   * land tomorrow. A badge that appeared only once somebody had visited a
   * setting would leave the rule invisible for almost every account while it was
   * already in force.
   *
   * Null only when the user belongs to no organisation at all.
   */
  defaultTenantId: string | null
  /**
   * Make an organisation the one this user lands in. The owner's *"I should have
   * option to change the default org"*, 2026-09-10.
   *
   * Hands back the whole failure rather than a boolean, for the reason `select`
   * does: the switcher shows the reason inline beside the row, and "could not
   * set default" covers a 403 from a revoked membership and a dropped
   * connection with one unactionable sentence.
   */
  setDefault: (tenantId: string) => Promise<ApiResult<void>>
  /**
   * Add an organisation the app has just been handed, without waiting for a
   * refetch. Creating one and accepting an invitation both return the tenant
   * *and* select it on the session, so at that moment both halves are known —
   * and navigating into the shell before a `reload()` lands would bounce off
   * `RequireTenant` and back to the picker.
   */
  adopt: (row: TenantMembership) => void
  reload: () => void
}

export const TenantContext = createContext<TenantValue | null>(null)

export function useTenants(): TenantValue {
  const value = useContext(TenantContext)
  if (!value) throw new Error('useTenants was called outside <TenantProvider>')
  return value
}

/**
 * The active organisation, for screens the tenant guard has already vetted.
 *
 * Throws rather than returning null for the same reason as `useCurrentUser`:
 * the router has made the null case unreachable, and a screen that renders with
 * an empty organisation name is a wiring bug that should be loud.
 */
export function useActiveTenant(): { tenant: Tenant; membership: Membership; role: Role } {
  const { active } = useTenants()
  if (!active) throw new Error('useActiveTenant was called outside a tenant-scoped route')
  return { tenant: active.tenant, membership: active.membership, role: active.membership.role }
}

/**
 * What the caller may do, as the server resolved it.
 *
 * **This replaced a hand-written copy of the permission matrix** (SHELL-26). The
 * old table mirrored `api/app/tenancy/roles.py` role by role, with a comment
 * explaining that OpenAPI describes shapes rather than authorisation so there was
 * nothing to generate it from. That was true, and it stopped being the point: a
 * custom role's permissions are not a property of its *name* — they are the union
 * of the pages the tenant ticked — so no static table here could know them.
 *
 * The server sends the resolved set on the membership instead. This is still only
 * so the UI does not offer a button that is going to 403; the refusal is the
 * server's, and `TenantMember.may` reads the same set this list came from.
 */
export function may(membership: Membership, permission: string): boolean {
  // `?? []` because the field is optional in the schema: most responses answer
  // with a membership whose grants were not resolved, and an unresolved one
  // offers nothing — the safe direction, since the server refuses regardless.
  return (membership.permissions ?? []).includes(permission)
}

/**
 * Whether the caller's role reaches a screen (SHELL-26).
 *
 * A tenant administrator reaches every page and holds none by grant — `pages` is
 * empty for them, and that is not the same as *no access*. The kind is checked
 * first for exactly that reason.
 */
export function maySee(membership: Membership, page: string): boolean {
  return membership.role === 'tenant_admin' || (membership.pages ?? []).includes(page)
}
